export const NEW_ID_SENTINEL = 'new';

const DRAFT_ID_SEPARATOR = '~';

/**
 * Is this column id a CREATE column rather than a record?
 *
 * `new` is the canonical one — the id every "Neu" affordance passes and the one a
 * deep link carries. Later generations (`new~2`, `new~3`, …) exist because a create
 * column must be able to be REPLACED by a different create column at the same
 * position, and identity is what makes that a replacement rather than a reuse: the
 * strip keys a column by `type:index`, deliberately reusing the component when a
 * record id swaps in place (a list-row switch keeps its in-flight queries). For a
 * create column that reuse is wrong — the outgoing panel would keep its references
 * into state that now belongs to the draft it just became.
 */
export const isDraftColumnId = (id: string): boolean =>
  id === NEW_ID_SENTINEL || id.startsWith(NEW_ID_SENTINEL + DRAFT_ID_SEPARATOR);

/**
 * Flex `order` for the cascade row's children.
 *
 * The row is one flex line and the draft boundary has to be able to sit BETWEEN two
 * cascade columns — which its DOM position cannot express, because the columns come from
 * a `TransitionGroup` the boundary must stay out of (as a group child it was animated
 * as a column and its leaves never settled). `order` decouples the two, so the boundary
 * can be rendered anywhere and drawn in the right place.
 *
 * Every child needs a value: an item without one keeps the default `0` and would jump
 * ahead of anything ordered after it.
 *
 * Columns take every SECOND slot so the odd numbers between them are free for the
 * boundary.
 */
export const ROW_ORDER = {
  leftSidebar: 0,
  listPane: 2,
  /** The nth cascade column. */
  column: (index: number): number => 10 + index * 2,
  /** Immediately before the nth cascade column. */
  beforeColumn: (index: number): number => 10 + index * 2 - 1,
  /** The draft zone, past every cascade column the grammar can hold (MAX_SEGMENTS). */
  zone: 200,
} as const;

/** The id for the nth create column of a type in this session; 1 is `new`. */
export const draftColumnId = (generation: number): string =>
  generation <= 1 ? NEW_ID_SENTINEL : `${NEW_ID_SENTINEL}${DRAFT_ID_SEPARATOR}${generation}`;

/**
 * Sentinel `parentIndex` meaning "open at the page/root level" — the root list's
 * single child slot. `open()` treats it as a re-root.
 */
export const ROOT_PARENT = -1;

/**
 * A single detail column in the cascade.
 *
 * The cascade is a **fan-out tree** (ADR-0027): the array is a depth-first
 * pre-order flattening of that tree. `parent` is the flat index of the column's
 * parent (`undefined` for the root column), and `sourceListKey` identifies which
 * source list within the parent this column was opened from. The invariant is
 * **at most one live child per `(parent, sourceListKey)` slot**.
 */
export interface CascadeColumn {
  type: string;
  id: string;
  tab?: string;
  /**
   * Flat index of the parent column in the same array. `undefined` for the root
   * column only. A parent always precedes its descendants (pre-order), so this
   * index is always smaller than the column's own index.
   */
  parent?: number;
  /**
   * Stable per-list key identifying which source list within the parent this
   * column was opened from — the second half of the `(parent, sourceListKey)`
   * slot identity. `undefined` for the root column only.
   */
  sourceListKey?: string;
}

export type CascadeStack = readonly CascadeColumn[];

export interface CascadeRef {
  type: string;
  id: string;
}

/** The record to open as a column (no tree linkage — the reducer assigns it). */
export interface OpenColumn {
  type: string;
  id: string;
  tab?: string;
}

export interface OpenRequest {
  /**
   * Flat index of the parent column, or {@link ROOT_PARENT} to open at the
   * page/root level (re-root).
   */
  parentIndex: number;
  /**
   * Stable per-list key for the slot within the parent. Ignored when opening at
   * the root level. `undefined` is the parent's **default (unnamed) child slot** —
   * the linear/degenerate case every existing workspace uses (one child per
   * parent). Fan-out verticals (RS-007) pass an explicit key per source list so a
   * parent can hold several sibling children at once.
   */
  sourceListKey?: string;
  /** The record to open. */
  column: OpenColumn;
}

/**
 * How an {@link OpenRequest} resolved:
 * - `retrigger` — the `{type,id}` was already open anywhere; nothing changed
 *   structurally (the UI pulses instead of opening a duplicate).
 * - `append` — the target slot was empty; a new child column was added.
 * - `replace` — the target slot held a different record; its child was swapped
 *   and that child's subtree discarded (siblings untouched).
 */
export type OpenOutcome = 'retrigger' | 'append' | 'replace';

export interface OpenResult {
  tree: CascadeColumn[];
  outcome: OpenOutcome;
}
